import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Pagination,
  Table,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as Yup from 'yup';
import { FaSearch, FaTrash, FaFileExcel } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import AuthStorage from '~/Utils/AuthStorage';
import * as api from '~/Services/Service';

const ITENS_POR_PAGINA = 20;

const TIPO_ENVIO_OPTIONS = [
  { value: null, label: 'Todos' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'SMS', label: 'SMS' },
];

const tableStyles = {
  wrapper: {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #dee2e6',
  },
  header: {
    backgroundColor: '#0747a6',
    color: '#fff',
    borderColor: '#0747a6',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    cursor: 'pointer',
  },
  cell: {
    fontSize: '13px',
    verticalAlign: 'middle',
  },
};

const COLUNAS = [
  { key: 'nome_familia',          label: 'Empreendimento',    align: 'left'   },
  { key: 'codigo_grupo_empresa',  label: 'GE',                align: 'center', width: '60px'  },
  { key: 'codigo_conta',          label: 'Conta',             align: 'center', width: '70px'  },
  { key: 'razao_social',          label: 'Nome',              align: 'left'   },
  { key: 'mapa1',                 label: 'QD',                align: 'center', width: '60px'  },
  { key: 'mapa2',                 label: 'LT',                align: 'center', width: '60px'  },
  { key: 'tipo_envio',            label: 'Tipo do Envio',     align: 'center', width: '110px' },
  { key: 'data_envio',            label: 'Data do Envio',     align: 'center', width: '130px' },
  { key: 'status_envio',          label: 'Status',            align: 'left',   width: '140px' },
  { key: 'ocorrencia_erro',       label: 'Ocorrência do Erro',align: 'left'   },
];

const schemaValidacao = Yup.object().shape({
  dataInicio: Yup.date()
    .required('Data de início é obrigatória')
    .max(Yup.ref('dataFim'), 'Data inicial deve ser menor ou igual a data final.'),
  dataFim: Yup.date().required('Data de fim é obrigatória'),
});

const ComunicadoQuitacao = () => {
  const hoje = moment().format('YYYY-MM-DD');

  const [dataInicio, setDataInicio] = useState(hoje);
  const [dataFim, setDataFim] = useState(hoje);
  const [geOpcoes, setGeOpcoes] = useState([]);
  const [geSelecionado, setGeSelecionado] = useState(null);
  const [clienteOpcoes, setClienteOpcoes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [tipoEnvio, setTipoEnvio] = useState(TIPO_ENVIO_OPTIONS[0]);
  const [resultados, setResultados] = useState([]);
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });

  const handleOrdenar = (key) => {
    setOrdenacao((prev) => ({
      coluna: key,
      direcao: prev.coluna === key && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
    setPagina(1);
  };

  const resultadosOrdenados = (() => {
    if (!ordenacao.coluna) return resultados;
    return [...resultados].sort((a, b) => {
      const valA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
      const valB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';
      if (typeof valA === 'number' && typeof valB === 'number') {
        return ordenacao.direcao === 'asc' ? valA - valB : valB - valA;
      }
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const renderSortIcon = (key) => {
    if (ordenacao.coluna !== key) {
      return <span style={{ opacity: 0.4, marginLeft: 4 }}>↕</span>;
    }
    return (
      <span style={{ opacity: 1, marginLeft: 4 }}>
        {ordenacao.direcao === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Carrega GEs autorizados do usuário ao montar
  useEffect(() => {
    const user = AuthStorage.getUser();
    if (!user) return;
    api
      .listarGrupoEmpresaUsuario(user.usuario)
      .then((res) => {
        const lista = (res || []).map((ge) => ({
          value: ge.codigo || ge.codigo_grupo_empresa,
          label: `${ge.codigo || ge.codigo_grupo_empresa} - ${ge.apelido || ge.nome || ''}`,
        }));
        setGeOpcoes(lista);
      })
      .catch(() => toast.error('Erro ao carregar empreendimentos'));
  }, []);

  // Cascata GE → Clientes
  useEffect(() => {
    if (!geSelecionado) {
      setClienteOpcoes([]);
      setClienteSelecionado(null);
      return;
    }
    let ativo = true;
    setClientesLoading(true);
    api
      .obterClientesComunicadoQuitacao(geSelecionado.value)
      .then((res) => {
        if (!ativo) return;
        const lista = (res || []).map((c) => ({
          value: c.codigo_conta,
          label: `${c.codigo_conta} - ${c.apelido_conta || ''}`,
        }));
        setClienteOpcoes(lista);
      })
      .catch(() => {
        if (!ativo) return;
        toast.error('Erro ao carregar clientes');
      })
      .finally(() => {
        if (ativo) setClientesLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [geSelecionado]);

  const validarFiltros = async () => {
    try {
      await schemaValidacao.validate({ dataInicio, dataFim });
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  const handleBuscar = async () => {
    const valido = await validarFiltros();
    if (!valido) return;

    setLoading(true);
    setPagina(1);
    try {
      const params = {
        dataInicio,
        dataFim,
        codigoGe: geSelecionado ? geSelecionado.value : null,
        codigoConta: clienteSelecionado ? clienteSelecionado.value : null,
        tipoEnvio: tipoEnvio && tipoEnvio.value ? tipoEnvio.value : null,
      };
      Object.keys(params).forEach((k) => {
        if (params[k] == null) delete params[k];
      });
      const res = await api.listarComunicadosQuitacao(params);
      setResultados(res || []);
      setConsultaRealizada(true);
      if (!res || res.length === 0) {
        toast.info('Nenhum comunicado encontrado para os filtros informados.');
      }
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao consultar comunicados de quitação.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = () => {
    setDataInicio(hoje);
    setDataFim(hoje);
    setGeSelecionado(null);
    setClienteSelecionado(null);
    setClienteOpcoes([]);
    setTipoEnvio(TIPO_ENVIO_OPTIONS[0]);
    setResultados([]);
    setConsultaRealizada(false);
    setPagina(1);
  };

  const handleGeChange = (opcao) => {
    setGeSelecionado(opcao);
    setClienteSelecionado(null);
  };

  const handleExportarCSV = () => {
    const headers = [
      'Empreendimento',
      'GE',
      'Conta',
      'Nome',
      'QD',
      'LT',
      'Tipo do Envio',
      'Data do Envio',
      'Status',
      'Ocorrência do Erro',
    ];
    const linhas = resultados.map((item) => [
      item.nome_familia != null ? item.nome_familia : '',
      item.codigo_grupo_empresa != null ? item.codigo_grupo_empresa : '',
      item.codigo_conta != null ? item.codigo_conta : '',
      item.razao_social != null ? item.razao_social : '',
      item.mapa1 != null ? item.mapa1 : '',
      item.mapa2 != null ? item.mapa2 : '',
      item.tipo_envio != null ? item.tipo_envio : '',
      item.data_envio ? moment(item.data_envio).format('DD/MM/YYYY HH:mm') : '',
      item.status_envio != null ? item.status_envio : '',
      item.ocorrencia_erro != null ? item.ocorrencia_erro : '',
    ]);
    const csv = [headers, ...linhas]
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
      )
      .join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comunicado-quitacao-${hoje}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Paginação client-side (sobre os dados já ordenados)
  const totalRegistros = resultadosOrdenados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / ITENS_POR_PAGINA));
  const inicio = (pagina - 1) * ITENS_POR_PAGINA;
  const dadosPaginados = resultadosOrdenados.slice(inicio, inicio + ITENS_POR_PAGINA);
  const registroInicio = totalRegistros === 0 ? 0 : inicio + 1;
  const registroFim = Math.min(pagina * ITENS_POR_PAGINA, totalRegistros);

  const renderPaginacao = () => {
    if (!consultaRealizada || totalRegistros === 0) return null;

    const paginas = [];
    const maxVisiveis = 5;
    let paginaInicio = Math.max(1, pagina - Math.floor(maxVisiveis / 2));
    const paginaFim = Math.min(totalPaginas, paginaInicio + maxVisiveis - 1);
    if (paginaFim - paginaInicio + 1 < maxVisiveis) {
      paginaInicio = Math.max(1, paginaFim - maxVisiveis + 1);
    }

    const pagList = [];
    for (let i = paginaInicio; i <= paginaFim; i++) {
      pagList.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2 border-top">
        <small className="text-muted">
          Mostrando {registroInicio}–{registroFim} de {totalRegistros}{' '}
          {totalRegistros === 1 ? 'registro' : 'registros'}
        </small>
        <Pagination className="mb-0" size="sm">
          <Pagination.First onClick={() => setPagina(1)} disabled={pagina <= 1} />
          <Pagination.Prev onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1} />
          {paginaInicio > 1 && <Pagination.Ellipsis disabled />}
          {pagList.map((p) => (
            <Pagination.Item
              key={p}
              active={p === pagina}
              onClick={() => setPagina(p)}
            >
              {p}
            </Pagination.Item>
          ))}
          {paginaFim < totalPaginas && <Pagination.Ellipsis disabled />}
          <Pagination.Next
            onClick={() => setPagina(pagina + 1)}
            disabled={pagina >= totalPaginas}
          />
          <Pagination.Last
            onClick={() => setPagina(totalPaginas)}
            disabled={pagina >= totalPaginas}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="px-3 py-3">
      <style>
        {`
          .tabela-comunicado-quitacao tbody tr:hover {
            background-color: #e8f4ff !important;
          }

          @media (max-width: 768px) {
            .tabela-comunicado-quitacao thead {
              display: none;
            }
            .tabela-comunicado-quitacao tbody tr {
              display: block;
              margin-bottom: 1rem;
              border: 1px solid #dee2e6;
              border-radius: 8px;
              overflow: hidden;
            }
            .tabela-comunicado-quitacao tbody td {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem 1rem;
              border: none;
              border-bottom: 1px solid #eee;
              font-size: 13px;
            }
            .tabela-comunicado-quitacao tbody td:last-child {
              border-bottom: none;
            }
            .tabela-comunicado-quitacao tbody td::before {
              content: attr(data-label);
              font-weight: 600;
              color: #0747a6;
              margin-right: 1rem;
              flex-shrink: 0;
            }
          }
        `}
      </style>

      <Header title="Comunicado de Quitação — Enviadas" />

      {/* Card de Filtros */}
      <Card className="shadow-sm mb-3">
        <Card.Header
          className="py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body className="pt-3 pb-2">
          <Row className="align-items-end">
            {/* Período */}
            <Col xs={12} md={6} lg={3} className="mb-3">
              <Form.Label className="mb-1 small font-weight-bold">
                Data Início <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                style={{ height: 38 }}
              />
            </Col>
            <Col xs={12} md={6} lg={3} className="mb-3">
              <Form.Label className="mb-1 small font-weight-bold">
                Data Fim <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                style={{ height: 38 }}
              />
            </Col>

            {/* GE */}
            <Col xs={12} md={6} lg={3} className="mb-3">
              <Form.Label className="mb-1 small font-weight-bold">
                Empreendimento
              </Form.Label>
              <Select
                options={geOpcoes}
                value={geSelecionado}
                onChange={handleGeChange}
                isClearable
                isSearchable
                placeholder="Todos"
                noOptionsMessage={() => 'Nenhum empreendimento'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>

            {/* Cliente */}
            <Col xs={12} md={6} lg={3} className="mb-3">
              <Form.Label className="mb-1 small font-weight-bold">
                Cliente
              </Form.Label>
              <Select
                options={clienteOpcoes}
                value={clienteSelecionado}
                onChange={(opcao) => setClienteSelecionado(opcao)}
                isClearable
                isSearchable
                isDisabled={!geSelecionado}
                isLoading={clientesLoading}
                placeholder={geSelecionado ? 'Todos' : 'Selecione um empreendimento'}
                noOptionsMessage={() => 'Nenhum cliente encontrado'}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>

            {/* Tipo de Envio */}
            <Col xs={12} md={6} lg={3} className="mb-3">
              <Form.Label className="mb-1 small font-weight-bold">
                Tipo de Envio
              </Form.Label>
              <Select
                options={TIPO_ENVIO_OPTIONS}
                value={tipoEnvio}
                onChange={(opcao) => setTipoEnvio(opcao)}
                placeholder="Todos"
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>

            {/* Botões */}
            <Col xs={12} md={6} lg={3} className="mb-3 d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleBuscar}
                disabled={loading}
                className="mr-2"
              >
                <FaSearch className="mr-1" />
                Buscar
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleLimpar}
                disabled={loading}
              >
                <FaTrash className="mr-1" />
                Limpar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Grid de Resultados */}
      {loading && <Loading />}

      {!loading && consultaRealizada && (
        <Card className="shadow-sm">
          <Card.Header
            className="py-2 d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
          >
            <strong>Resultados</strong>
            {resultados.length > 0 && (
              <Button
                variant="success"
                size="sm"
                onClick={handleExportarCSV}
                title="Exportar CSV"
              >
                <FaFileExcel className="mr-1" />
                Exportar CSV
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <div style={tableStyles.wrapper}>
              <Table
                bordered
                hover
                responsive
                className="mb-0 tabela-comunicado-quitacao"
              >
                <thead>
                  <tr>
                    {COLUNAS.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          ...tableStyles.header,
                          textAlign: col.align,
                          width: col.width || 'auto',
                        }}
                        onClick={() => handleOrdenar(col.key)}
                        title={`Ordenar por ${col.label}`}
                      >
                        {col.label}
                        {renderSortIcon(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dadosPaginados.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4 text-muted">
                        Nenhum registro encontrado
                      </td>
                    </tr>
                  ) : (
                    dadosPaginados.map((item) => (
                      <tr
                        key={`${item.codigo_grupo_empresa}-${item.codigo_conta}-${item.mapa1}-${item.mapa2}-${item.tipo_envio}-${item.data_envio}`}
                      >
                        <td data-label="Empreendimento" style={tableStyles.cell}>
                          {item.nome_familia || '-'}
                        </td>
                        <td
                          data-label="GE"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.codigo_grupo_empresa || '-'}
                        </td>
                        <td
                          data-label="Conta"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.codigo_conta || '-'}
                        </td>
                        <td data-label="Nome" style={tableStyles.cell}>
                          {item.razao_social || '-'}
                        </td>
                        <td
                          data-label="QD"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.mapa1 || '-'}
                        </td>
                        <td
                          data-label="LT"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.mapa2 || '-'}
                        </td>
                        <td
                          data-label="Tipo do Envio"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.tipo_envio || '-'}
                        </td>
                        <td
                          data-label="Data do Envio"
                          style={{ ...tableStyles.cell, textAlign: 'center' }}
                        >
                          {item.data_envio
                            ? moment(item.data_envio).format('DD/MM/YYYY HH:mm')
                            : '-'}
                        </td>
                        <td data-label="Status" style={tableStyles.cell}>
                          {item.status_envio || '-'}
                        </td>
                        <td data-label="Ocorrência do Erro" style={tableStyles.cell}>
                          {item.ocorrencia_erro || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {renderPaginacao()}
        </Card>
      )}
    </Container>
  );
};

export default ComunicadoQuitacao;
